import React, { useCallback, useEffect, useState } from "react";
import { Route, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { ProviderLogo } from "@/components/ProviderLogo";

export const getLedgers = () => api.get("/ledgers").then((r) => r.data);
const setDefault = (provider) => api.put("/ledgers/default", { provider }).then((r) => r.data);
const setSupplier = (supplier, provider) => api.put("/ledgers/supplier", { supplier, provider }).then((r) => r.data);

const Sel = ({ value, onChange, options, allowDefault, testId, disabled }) => (
  <select value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value || null)} data-testid={testId} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
    <option value="">{allowDefault ? "Follow default" : "— choose —"}</option>
    {options.map((c) => <option key={c.provider} value={c.provider}>{c.label}{c.tenant_name ? ` · ${c.tenant_name.trim()}` : ""}</option>)}
  </select>
);

export const LedgerRouting = ({ onChanged }) => {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => getLedgers().then(setD).catch(() => setD(false)), []);
  useEffect(() => { load(); }, [load]);
  if (!d || !d.multi) return null;
  const run = async (fn, msg) => { setBusy(true); try { setD(await fn()); toast.success(msg); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); } };
  const rows = d.suppliers.filter((s) => !q || s.supplier.toLowerCase().includes(q.toLowerCase()));
  return (
    <section className="rounded-xl border border-primary/30 bg-primary/5 p-5" data-testid="ledger-routing">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Route className="h-5 w-5" /></span>
          <div>
            <div className="font-medium">Two ledgers — where do approved invoices go?</div>
            <p className="text-xs text-muted-foreground">You have {d.connected.length} accounting systems connected ({d.connected.map((c) => c.label).join(" and ")}). Every invoice is sent to one of them: the supplier's own rule if you set one, otherwise the default below.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm" data-testid="ledger-default-row">
          <span className="text-muted-foreground">Default</span>
          <Sel value={d.default} options={d.connected} disabled={busy} testId="ledger-default-select" onChange={(v) => run(() => setDefault(v), v ? "Default ledger saved" : "Default cleared")} />
        </div>
      </div>
      {!d.default && <p className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400" data-testid="ledger-no-default">Pick a default — until you do, only suppliers with their own rule are sent anywhere.</p>}
      <div className="mt-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>Supplier rules · {d.overrides} set</span>
          <label className="relative"><Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a supplier" data-testid="ledger-supplier-search" className="h-8 rounded-md border border-input bg-background pl-7 pr-2 text-xs" /></label>
        </div>
        <div className="d20-scroll max-h-72 overflow-auto rounded-lg border border-border bg-card">
          {rows.length === 0 && <div className="p-3 text-xs text-muted-foreground">No suppliers match.</div>}
          {rows.map((s) => (
            <div key={s.key} className="flex items-center justify-between gap-3 border-b border-border px-3 py-2 last:border-0" data-testid={`ledger-supplier-${s.key.replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="min-w-0"><div className="truncate text-sm">{s.supplier}</div><div className="text-[11px] text-muted-foreground">{s.count} invoice{s.count === 1 ? "" : "s"}{s.ledger ? "" : d.default_label ? ` · follows default (${d.default_label})` : ""}</div></div>
              <div className="flex items-center gap-2">{s.ledger && <ProviderLogo provider={s.ledger} className="h-5 w-5" />}<Sel value={s.ledger} options={d.connected} allowDefault disabled={busy} testId={`ledger-supplier-select-${s.key.replace(/[^a-z0-9]+/g, "-")}`} onChange={(v) => run(() => setSupplier(s.supplier, v), v ? `${s.supplier} → ${d.connected.find((c) => c.provider === v)?.label}` : `${s.supplier} follows the default`)} /></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const LedgerChip = ({ inv }) => {
  const [d, setD] = useState(null);
  useEffect(() => { getLedgers().then(setD).catch(() => setD(false)); }, []);
  if (!d || !d.multi) return null;
  const target = inv.ledger || d.default;
  const c = d.connected.find((x) => x.provider === target);
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs" data-testid="ledger-chip">
      <Route className="h-3.5 w-3.5 text-primary" />
      {c ? <span>Goes to <b>{c.label}</b> · {inv.ledger ? "supplier rule" : "workspace default"}</span> : <span className="text-amber-600 dark:text-amber-400">No ledger chosen for this supplier — set a default under Rules &amp; Accounts → Accounting</span>}
      {c && <ProviderLogo provider={c.provider} className="ml-auto h-4 w-4" />}
    </div>
  );
};
